import {
  useCallback, useEffect, useRef, useState,
} from 'react';

// Ported from Frontend-Platform `libs/ui/src/hooks/useMediaQuery/useMediaQuery.ts`, with the
// `useEffectEvent`-based change handler (React 19-only) swapped for a plain ref, since Daffy is
// on React 18.

// ============================================================
//     CONSTANTS
// ============================================================
/**
 * Matches the FE2.0 `SM` breakpoint (`jslib/ui/src/theme/responsive/responsive.ts`), so Daffy's
 * sidebar collapses into a mobile drawer at the same viewport width as frontend-dashboard's.
 */
export const SIDEBAR_MOBILE_QUERY = '(width < 768px)';

// ============================================================
//     TYPES
// ============================================================
interface UseMediaQueryOpts {
  /**
   * Custom implementation of matchMedia. Useful for handling an iframe content window, or for tests.
   */
  customMatchMedia?: typeof window.matchMedia;

  /**
   * Returns a default matches during the initial render.
   *
   * @default false
   */
  defaultMatches?: boolean;
}

// ============================================================
//     HELPERS
// ============================================================
const formatQuery = (queryStr: string): string => queryStr.replace('@media', '').trim();

// ============================================================
//     HOOK
// ============================================================
/**
 * A hook that returns whether a given media query matches the current viewport
 *
 * @param query The media query to test (e.g. '(width < 768px)')
 * @returns boolean indicating whether the media query matches
 */
export const useMediaQuery = (query: string, opts?: UseMediaQueryOpts): boolean => {
  const { customMatchMedia, defaultMatches = false } = opts ?? {};

  const getMatches = useCallback(
    (queryStr: string): boolean => {
      const formattedQuery = formatQuery(queryStr);

      if (customMatchMedia) {
        return customMatchMedia(formattedQuery).matches;
      }

      // jsdom (Daffy's Vitest environment) doesn't implement `matchMedia` — fall back to
      // `defaultMatches` there, same as the `typeof window === 'undefined'` SSR case, rather
      // than throwing on every render of every Sidebar in the test suite.
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return defaultMatches;
      }

      return window.matchMedia(formattedQuery).matches;
    },
    [customMatchMedia, defaultMatches],
  );

  const [matches, setMatches] = useState<boolean>(() => getMatches(query));

  const getMatchesRef = useRef(getMatches);
  const queryRef = useRef(query);
  useEffect(() => {
    getMatchesRef.current = getMatches;
    queryRef.current = query;
  });

  useEffect(() => {
    if (!customMatchMedia && typeof window.matchMedia !== 'function') return undefined;

    const formattedQuery = formatQuery(query);

    const matchMedia: MediaQueryList = customMatchMedia
      ? customMatchMedia(formattedQuery)
      : window.matchMedia(formattedQuery);

    const handleChange = () => {
      setMatches(getMatchesRef.current(queryRef.current));
    };

    matchMedia.addEventListener('change', handleChange);
    handleChange();

    return () => {
      matchMedia.removeEventListener('change', handleChange);
    };
  }, [query, customMatchMedia]);

  return matches;
};
